<script lang="ts">
  import { modifyImageUrl } from '$utils/formatters';
  import Cashback from '$components/+cashback.svelte';
  import { browser } from '$app/environment';
  import { page } from '$app/stores';
  import type { Page } from '@sveltejs/kit'; // Import Page type for store
  import { updateUrlParams } from '$utils/updateUrlParams';
  import { afterNavigate, goto } from '$app/navigation';
  import { onMount } from 'svelte'; // --- Type Definitions ---
  import type { LocationItem, ModalEventDetail } from '$types';

  export let item: LocationItem;

  export let href: string;

  export let promoted: boolean = false; // --- State ---

  let isOnFindPartnersPage: boolean = false;

  let imageLoaded: boolean = false;

  let imageObj: HTMLImageElement | null = null; // Use null for initial state

  $: if (browser) {
    isOnFindPartnersPage = ($page as Page<{}>).url.pathname === '/find-partnere'; // Type assertion for $page
  }

  function handleCardClick(uuid: string): void {
    if (!browser) {
      goto('/find-partners/' + uuid, { replaceState: true, noScroll: true, keepFocus: false });
      return;
    }

    if (isOnFindPartnersPage) {
      const event = new CustomEvent<ModalEventDetail>('open-modal', { detail: { uuid } });
      window.dispatchEvent(event);
    } else {
      // Update URL parameters on other pages
      updateUrlParams({ 'location-uuid': uuid });
    }
  }

  async function loadImage(): Promise<void> {
    // Only preload on the client and specific page
    if (!browser || !isOnFindPartnersPage || !item.images?.hero) {
      if (!isOnFindPartnersPage) {
        imageLoaded = true;
      }

      return;
    }

    try {
      const img = new Image();

      img.onload = () => {
        imageLoaded = true;
        imageObj = img; // Store the loaded image object
      };

      img.src = modifyImageUrl(item.images.hero) ?? '';
    } catch (error) {
      console.error('Error during image loading setup:', error);

      imageLoaded = true; // Ensure UI isn't stuck in loading on error
    }
  }

  afterNavigate(() => {
    if (isOnFindPartnersPage) {
      imageLoaded = false; // Reset loading state
      imageObj = null; // Clear previous image object
      loadImage();
    } else {
      imageLoaded = true;
    }
  });

  onMount(() => {
    loadImage();
  });
</script>

{#if !imageLoaded && isOnFindPartnersPage}
  <div class="location-card placeholder">
    <div class="card-hero">
      <div class="card-logo"></div>
    </div>
    <div class="content">
      <div class="line"></div>
      <div class="line"></div>
    </div>
  </div>
{:else}
  <div class="location-card">
    <a {href} on:click|preventDefault={() => handleCardClick(item.uuid)} data-sveltekit-noscroll data-sveltekit-preload-data aria-label={item?.name}>
      {#if item.rewards?.cashback_text}
        <Cashback cashback_text={item.rewards.cashback_text} />
      {:else if item.rewards?.first_visit?.rate != null}
        <Cashback cashback={item.rewards.first_visit.rate} />
      {/if}
      <div class="card-hero">
        <picture>
          <img src={imageObj ? imageObj.src : modifyImageUrl(item.images?.hero ?? '')} alt={item?.name ?? 'Location'} />
        </picture>
        <div class="card-logo">
          <img src={modifyImageUrl(item.images?.logo ?? '', { width: 200 })} alt={item?.name ?? 'Logo'} />
        </div>
        {#if promoted}
          <div class="card-logo promoted">
            <span>Sponsoreret</span>
          </div>
        {/if}
      </div>
      <div class="content">
        <h3>{item.name}</h3>
        <div class="tags">
          {#if item.type_object?.name}
            <span class="type">{item.type_object.name}</span>
          {/if}

          {#if item.category?.name}
            <span class="category">{item.category.name}</span>
          {/if}
        </div>
      </div>
    </a>
  </div>
{/if}
